import React, { useEffect, useState } from 'react';
import { Card, Dropdown, Grid, Message } from 'semantic-ui-react';
import { H2, PageSection } from 'common/styles/page';
import { FieldItem, FieldLabel, FieldList, FieldValue, Subtitle } from '../../styles/styles';
import { primaryColor } from 'common/constants/globalstyles';
import { PeriodicTask } from '../types/PeriodicTask';
import { TaskEditModal } from './TaskEditModal';
import TaskHistoryModal from './TaskHistoryModal';
import TaskParameterModal from './TaskParameterModal';
import { processDateArithmetic } from '../utils/date-utils';
import TaskList from './TaskList';
import { getNextRunTime } from '../utils/cron-utils';
import TaskFilters from './TaskFilters';
import {
  useDeleteOrphanedTasksMutation,
  useDeleteTaskMutation,
  useGetDiagnosticsQuery,
  useGetSystemHealthDataQuery,
  useGetTaskExecutionMetricsQuery,
  useRunAnyTaskMutation,
  useRunPeriodicTaskMutation,
  useUpdateTaskFullMutation,
  useUpdateTaskMutation,
} from '../api/taskManagementApi';

const TasksManagement: React.FC = () => {
  const [selectedTask, setSelectedTask] = useState<PeriodicTask | null>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [isParameterModalVisible, setIsParameterModalVisible] = useState(false);
  const [selectedTaskForRun, setSelectedTaskForRun] = useState<{
    name: string;
    description: string;
    parameters: any[];
  } | null>(null);
  const [message, setMessage] = useState<{
    type: 'success' | 'error' | 'info';
    content: string;
  } | null>(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');

  // RTK Query hooks
  const {
    data: systemHealthData,
    isLoading: loading,
    error: systemHealthError,
    refetch: refetchSystemHealth,
  } = useGetSystemHealthDataQuery();

  const { data: diagnostics, isLoading: diagnosticsLoading } = useGetDiagnosticsQuery(undefined, {
    skip: !showDiagnostics,
  });

  const { data: taskMetrics, isLoading: metricsLoading } = useGetTaskExecutionMetricsQuery();

  const [updateTask, { isLoading: updateTaskLoading }] = useUpdateTaskMutation();
  const [updateTaskFull, { isLoading: updateTaskFullLoading }] = useUpdateTaskFullMutation();
  const [runPeriodicTask, { isLoading: runTaskLoading }] = useRunPeriodicTaskMutation();
  const [runAnyTask, { isLoading: runAnyTaskLoading }] = useRunAnyTaskMutation();
  const [deleteOrphanedTasks, { isLoading: deleteOrphanedLoading }] =
    useDeleteOrphanedTasksMutation();
  const [deleteTask, { isLoading: deleteTaskLoading }] = useDeleteTaskMutation();

  const updating =
    updateTaskLoading ||
    updateTaskFullLoading ||
    runTaskLoading ||
    runAnyTaskLoading ||
    deleteOrphanedLoading ||
    deleteTaskLoading;

  // Handle system health query errors
  useEffect(() => {
    if (systemHealthError) {
      setMessage({ type: 'error', content: 'Failed to fetch tasks' });
    }
  }, [systemHealthError]);

  const handleDeleteOrphanedTasks = async () => {
    if (
      !window.confirm(
        'Are you sure you want to delete all old tasks? This action cannot be undone.'
      )
    ) {
      return;
    }

    try {
      const result = await deleteOrphanedTasks().unwrap();
      setMessage({ type: 'success', content: result.message });
    } catch (error) {
      setMessage({ type: 'error', content: 'Failed to delete old tasks' });
    }
  };

  const handleDeleteTask = async (taskId: number, taskName: string) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the task "${taskName}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await deleteTask({ id: taskId }).unwrap();
      setMessage({ type: 'success', content: `Task "${taskName}" deleted successfully` });
    } catch (error) {
      setMessage({ type: 'error', content: `Failed to delete task "${taskName}"` });
    }
  };

  const handleEdit = (task: any) => {
    setSelectedTask(task);
    setIsEditModalVisible(true);
  };

  const handleViewHistory = (taskId: number) => {
    // Find the task object by ID to set as selected task
    const allTasks = getAllTasksUnified();
    const task = allTasks.find(t => {
      // For periodic tasks, use the periodic_task.id
      if (t.is_periodic && t.periodic_task) {
        return t.periodic_task.id === taskId;
      }
      // For registry tasks, we need to find by registry_task.id or name
      if (!t.is_periodic && t.registry_task) {
        return t.registry_task.id === taskId || t.registry_task.name === taskId;
      }
      return false;
    });
    setSelectedTask(task);
    setIsHistoryModalVisible(true);
  };

  const handleToggleEnabled = async (task: any) => {
    try {
      await updateTask({ id: task.id, enabled: !task.enabled }).unwrap();
      setMessage({
        type: 'success',
        content: `Task ${task.enabled ? 'disabled' : 'enabled'} successfully`,
      });
    } catch (error) {
      setMessage({ type: 'error', content: 'Failed to update task status' });
    }
  };

  const handleRunTask = async (task: any) => {
    try {
      let kwargs = {};

      try {
        if (task.kwargs && task.kwargs.trim()) {
          const processedKwargs = processDateArithmetic(task.kwargs);
          kwargs = JSON.parse(processedKwargs);
        }
      } catch (error) {
        console.warn('Invalid kwargs JSON, using empty object:', error);
      }

      const result = await runPeriodicTask({
        task_name: task.task,
        arguments: [],
        kwargs: kwargs,
      }).unwrap();

      if (result.success) {
        setMessage({
          type: 'success',
          content: `Task ${task.name} queued successfully! Task ID: ${result.task_id}`,
        });
      } else {
        setMessage({ type: 'error', content: `Failed to queue task: ${result.error}` });
      }
    } catch (error) {
      setMessage({ type: 'error', content: 'Failed to run task' });
    }
  };

  const handleRunAnyTask = async (taskName: string, taskDescription: string) => {
    // Check if this task has parameters by looking at the registry tasks
    const registryTasks = systemHealthData?.registry_tasks || [];
    const taskInfo = registryTasks.find((task: any) => task.name === taskName);
    
    // Check if task has default_kwargs (indicating it needs parameters)
    // Important: Even if values are null, if keys exist, we should show the parameters
    const defaultKwargs = taskInfo?.default_kwargs || {};
    const hasParameters = defaultKwargs && typeof defaultKwargs === 'object' && Object.keys(defaultKwargs).length > 0;
    
    if (hasParameters) {
      // Show parameter modal
      setSelectedTaskForRun({
        name: taskName,
        description: taskDescription,
        parameters: Object.entries(defaultKwargs).map(([name, defaultValue]) => ({
          name,
          type: typeof defaultValue === 'boolean' ? 'boolean' : 
                typeof defaultValue === 'number' ? 'number' : 'string',
          required: defaultValue !== null && defaultValue !== undefined, // Required if not null/undefined
          description: `Parameter for ${taskName}`,
          default_value: defaultValue
        }))
      });
      setIsParameterModalVisible(true);
    } else {
      // Run task without parameters
      await executeTaskWithParameters(taskName, taskDescription, {});
    }
  };

  const executeTaskWithParameters = async (taskName: string, taskDescription: string, parameters: Record<string, any>) => {
    try {
      const result = await runAnyTask({
        task_name: taskName,
        arguments: [],
        kwargs: parameters,
      }).unwrap();

      if (result.success) {
        setMessage({
          type: 'success',
          content: `Task ${taskDescription} queued successfully! Task ID: ${result.task_id}`,
        });
      } else {
        setMessage({ type: 'error', content: `Failed to queue task: ${result.error}` });
      }
    } catch (error) {
      setMessage({ type: 'error', content: 'Failed to run task' });
    }
  };

  const handleRunTaskWithParameters = async (parameters: Record<string, any>) => {
    if (selectedTaskForRun) {
      await executeTaskWithParameters(selectedTaskForRun.name, selectedTaskForRun.description, parameters);
      setIsParameterModalVisible(false);
      setSelectedTaskForRun(null);
    }
  };

  const handleSaveEdit = async (formData: any) => {
    if (!selectedTask) return;

    try {
      const updateData: any = {
        name: formData.name,
        task: formData.task,
        enabled: formData.enabled,
        description: formData.description,
        args: formData.args || '[]',
        kwargs: formData.kwargs,
      };

      // Include schedule data if it exists
      if (formData.interval) {
        updateData.interval = formData.interval;
      }

      if (formData.crontab) {
        updateData.crontab = formData.crontab;
      }

      await updateTaskFull({ id: selectedTask.id, data: updateData }).unwrap();
      setMessage({ type: 'success', content: 'Task updated successfully' });
      setIsEditModalVisible(false);
    } catch (error) {
      setMessage({ type: 'error', content: 'Failed to update task' });
    }
  };

  const getScheduleDisplay = (task: any) => {
    if (task.interval) {
      return `Every ${task.interval.every} ${task.interval.period}(s)`;
    }
    if (task.crontab) {
      const { minute, hour, day_of_week, day_of_month, month_of_year } = task.crontab;
      return `${minute} ${hour} ${day_of_month} ${month_of_year} ${day_of_week}`;
    }
    return 'No schedule';
  };

  const getRegistryScheduleDisplay = (defaultSchedule: any) => {
    if (!defaultSchedule) return 'No schedule';

    if (defaultSchedule.type === 'interval') {
      const config = defaultSchedule.config;
      return `Every ${config.every} ${config.period}(s)`;
    } else if (defaultSchedule.type === 'crontab') {
      const config = defaultSchedule.config;
      return `${config.minute || '*'} ${config.hour || '*'} ${config.day_of_month || '*'} ${config.month_of_year || '*'} ${config.day_of_week || '*'}`;
    }

    return 'Custom schedule';
  };

  const getParametersDisplay = (task: any) => {
    const hasKwargs = task.kwargs && task.kwargs !== '{}' && task.kwargs.trim() !== '';

    if (!hasKwargs) {
      return 'No parameters';
    }

    return task.kwargs;
  };

  const getFilteredTasks = () => {
    let filtered = getAllTasksUnified();

    if (searchFilter) {
      filtered = filtered.filter(
        task =>
          task.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
          task.description.toLowerCase().includes(searchFilter.toLowerCase())
      );
    }

    if (categoryFilter) {
      filtered = filtered.filter(task => task.category === categoryFilter);
    }

    // Sort by periodic tasks first, then category, then alphabetically by name
    filtered.sort((a, b) => {
      // First sort by periodic tasks (periodic tasks come first)
      if (a.is_periodic !== b.is_periodic) {
        return a.is_periodic ? -1 : 1;
      }
      // Then sort by category
      if (a.category !== b.category) {
        return a.category.localeCompare(b.category);
      }
      // Finally sort by name within the same category
      return a.name.localeCompare(b.name);
    });

    return filtered;
  };

  const getTaskCategories = () => {
    const registryTasks = Array.isArray(systemHealthData?.registry_tasks)
      ? systemHealthData.registry_tasks
      : [];
    const periodicTasks = Array.isArray(systemHealthData?.periodic_tasks)
      ? systemHealthData.periodic_tasks
      : [];
    
    // Get categories from registry tasks (proper business categories)
    const registryCategories = Array.from(new Set(registryTasks.map(task => task.category)));
    
    // For periodic tasks, try to infer category from task name or add to maintenance
    const periodicCategories = periodicTasks.length > 0 ? ['maintenance'] : [];
    
    // Combine and deduplicate
    const allCategories = [...registryCategories, ...periodicCategories];
    return Array.from(new Set(allCategories)).sort();
  };

  const getAllTasksUnified = () => {
    const unifiedTasks: any[] = [];
    const seenTaskPaths = new Set<string>(); // Track task paths to prevent duplicates

    // First, add periodic tasks (these take precedence as they're active in the database)
    const periodicTasks = Array.isArray(systemHealthData?.periodic_tasks)
      ? systemHealthData.periodic_tasks
      : [];
    periodicTasks.forEach(task => {
      // Skip disabled system tasks (like celery.backend_cleanup)
      if ((task as any).is_system_task && !task.enabled) {
        return;
      }
      
      // Try to infer category from task name, default to maintenance
      let category = 'maintenance';
      if (task.task.includes('assessment')) category = 'assessments';
      else if (task.task.includes('email') || task.task.includes('message')) category = 'messaging';
      else if (task.task.includes('monitor') || task.task.includes('health')) category = 'monitoring';
      else if (task.task.includes('cleanup') || task.task.includes('maintenance')) category = 'maintenance';
      
      unifiedTasks.push({
        id: `periodic_${task.id}`,
        name: task.name,
        description: task.description,
        category: category,
        max_retries: 3,
        retry_delay: 60,
        timeout: null,
        requires_audit: true,
        ignore_result: false,
        has_schedule: true,
        is_periodic: true,
        enabled: task.enabled,
        schedule: getScheduleDisplay(task),
        parameters: getParametersDisplay(task),
        last_run: task.last_run_at ? new Date(task.last_run_at).toLocaleString() : 'Never',
        next_run: getNextRunTime(task),
        total_run_count: task.total_run_count,
        task_path: task.task,
        periodic_task: task,
        status: task.enabled ? 'enabled' : 'disabled',
      });
      
      // Mark this task path as seen
      seenTaskPaths.add(task.task);
    });

    // Then, add registry tasks (only if not already seen as periodic tasks)
    const registryTasks = Array.isArray(systemHealthData?.registry_tasks)
      ? systemHealthData.registry_tasks
      : [];
    registryTasks.forEach(task => {
      // Skip if this task already exists as a periodic task
      if (seenTaskPaths.has(task.name)) {
        return;
      }
      
      // Format parameters display from default_kwargs
      const formatParameters = (defaultKwargs: any) => {
        if (!defaultKwargs || Object.keys(defaultKwargs).length === 0) {
          return 'No parameters';
        }
        const paramEntries = Object.entries(defaultKwargs)
          .filter(([_, value]) => value !== null && value !== undefined)
          .map(([key, value]) => `${key}=${value}`);
        if (paramEntries.length === 0) {
          // All values are null/undefined, but still show the parameter names
          return Object.keys(defaultKwargs).join(', ');
        }
        return paramEntries.join(', ');
      };

      unifiedTasks.push({
        id: `registry_${task.name}`,
        name: task.name,
        description: task.description,
        category: task.category,
        max_retries: task.max_retries,
        retry_delay: task.retry_delay,
        timeout: task.timeout,
        requires_audit: task.requires_audit,
        ignore_result: task.ignore_result,
        has_schedule: task.has_schedule,
        default_schedule: task.default_schedule,
        is_periodic: false,
        enabled: false,
        schedule: task.default_schedule
          ? getRegistryScheduleDisplay(task.default_schedule)
          : 'No schedule',
        parameters: formatParameters(task.default_kwargs),
        last_run: 'Never',
        next_run: 'N/A',
        total_run_count: 0,
        task_path: task.name,
        registry_task: task,
        status: 'available',
      });
      
      // Mark this task path as seen
      seenTaskPaths.add(task.name);
    });

    // Finally, add orphaned tasks (these are always unique)
    const orphanedTasksArray = Array.isArray(systemHealthData?.orphaned_tasks)
      ? systemHealthData.orphaned_tasks
      : [];
    orphanedTasksArray.forEach(task => {
      unifiedTasks.push({
        id: `orphaned_${task.id}`,
        name: task.name,
        description: task.description || 'Orphaned task - code no longer exists',
        category: 'maintenance', // Orphaned tasks are maintenance issues
        max_retries: 3,
        retry_delay: 60,
        timeout: null,
        requires_audit: true,
        ignore_result: false,
        has_schedule: true,
        is_periodic: true,
        enabled: task.enabled,
        schedule: 'Orphaned - schedule may be invalid',
        parameters: 'No parameters',
        last_run: task.last_run_at ? new Date(task.last_run_at).toLocaleString() : 'Never',
        next_run: 'N/A',
        total_run_count: task.total_run_count,
        task_path: task.task,
        orphaned_task: task,
        status: 'orphaned',
      });
    });

    return unifiedTasks;
  };

  return (
    <PageSection padtop>
      {message && (
        <Message
          positive={message.type === 'success'}
          negative={message.type === 'error'}
          info={message.type === 'info'}
          onDismiss={() => setMessage(null)}
        >
          {message.content}
        </Message>
      )}

      <TaskFilters
        searchFilter={searchFilter}
        setSearchFilter={setSearchFilter}
        categoryFilter={categoryFilter}
        setCategoryFilter={setCategoryFilter}
        getTaskCategories={getTaskCategories}
        getFilteredTasks={getFilteredTasks}
        getAllTasksUnified={getAllTasksUnified}
        fetchAllTasks={refetchSystemHealth}
        loading={loading}
        extraControls={
          <Dropdown
            button
            icon="ellipsis vertical"
            floating
            className="icon"
            direction="left"
            style={{ width: '38px', height: '35px', borderRadius: '8px' }}
            onClick={e => e.stopPropagation()}
          >
            <Dropdown.Menu>
              <Dropdown.Item
                icon="bug"
                text={showDiagnostics ? 'Hide Diagnostics' : 'Show Diagnostics'}
                onClick={() => {
                  setShowDiagnostics(!showDiagnostics);
                }}
              />
              <Dropdown.Item
                icon="trash"
                text="Delete All Orphaned Tasks"
                onClick={handleDeleteOrphanedTasks}
              />
            </Dropdown.Menu>
          </Dropdown>
        }
      />

      {showDiagnostics && diagnostics && (
        <PageSection padtop>
          <PageSection padbottom>
            <Card fluid raised style={{ padding: '20px' }}>
              <Grid columns={2} stackable divided>
                <Grid.Row>
                  <Grid.Column>
                    <H2 padbottom color={primaryColor}>
                      Celery Tasks
                    </H2>
                    <FieldList>
                      <FieldItem>
                        <FieldLabel>Total</FieldLabel>
                        <FieldValue>{diagnostics.task_results.total}</FieldValue>
                      </FieldItem>
                      <FieldItem>
                        <FieldLabel>Last 7 days</FieldLabel>
                        <FieldValue>{diagnostics.task_results.recent_7_days}</FieldValue>
                      </FieldItem>
                    </FieldList>

                    <H2 padbottom padtop color={primaryColor}>
                      Configuration
                    </H2>
                    <FieldList>
                      <FieldItem>
                        <FieldLabel>Broker</FieldLabel>
                        <FieldValue>{diagnostics.celery_config.broker_url}</FieldValue>
                      </FieldItem>
                      <FieldItem>
                        <FieldLabel>Result Backend</FieldLabel>
                        <FieldValue>{diagnostics.celery_config.result_backend}</FieldValue>
                      </FieldItem>
                      <FieldItem>
                        <FieldLabel>Queue Prefix</FieldLabel>
                        <FieldValue>{diagnostics.celery_config.queue_prefix}</FieldValue>
                      </FieldItem>
                      <FieldItem>
                        <FieldLabel>Default Queue</FieldLabel>
                        <FieldValue>{diagnostics.celery_config.default_queue}</FieldValue>
                      </FieldItem>
                      <FieldItem>
                        <FieldLabel>Server Name</FieldLabel>
                        <FieldValue>{diagnostics.server_name}</FieldValue>
                      </FieldItem>
                    </FieldList>
                  </Grid.Column>

                  {/* Right column */}
                  <Grid.Column>
                    <H2 padbottom color={primaryColor}>
                      Periodic Tasks
                    </H2>
                    <FieldList>
                      <FieldItem>
                        <FieldLabel>Total</FieldLabel>
                        <FieldValue>{diagnostics.periodic_tasks.total}</FieldValue>
                      </FieldItem>
                      <FieldItem>
                        <FieldLabel>Enabled</FieldLabel>
                        <FieldValue>{diagnostics.periodic_tasks.enabled}</FieldValue>
                      </FieldItem>
                    </FieldList>

                    <H2 padbottom padtop color={primaryColor}>
                      Queues
                    </H2>
                    <FieldList>
                      {Object.entries(diagnostics.queues).map(([name, config]: [string, any]) => (
                        <FieldItem key={name}>
                          <FieldLabel>{name}</FieldLabel>
                          <FieldValue>{config.queue}</FieldValue>
                        </FieldItem>
                      ))}
                    </FieldList>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Card>
          </PageSection>
        </PageSection>
      )}

      <TaskList
        loading={loading}
        updating={updating}
        setMessage={setMessage}
        handleRunTask={handleRunTask}
        handleRunAnyTask={handleRunAnyTask}
        handleToggleEnabled={handleToggleEnabled}
        handleEdit={handleEdit}
        handleViewHistory={handleViewHistory}
        handleDeleteOrphanedTasks={handleDeleteOrphanedTasks}
        handleDeleteTask={handleDeleteTask}
        getFilteredTasks={getFilteredTasks}
        getAllTasksUnified={getAllTasksUnified}
        getRegistryScheduleDisplay={getRegistryScheduleDisplay}
        taskMetrics={taskMetrics?.task_metrics}
      />

      <TaskEditModal
        isEditModalVisible={isEditModalVisible}
        setIsEditModalVisible={setIsEditModalVisible}
        selectedTask={selectedTask}
        handleSaveEdit={handleSaveEdit}
        updating={updating}
      />

      <TaskHistoryModal
        isHistoryModalVisible={isHistoryModalVisible}
        setIsHistoryModalVisible={setIsHistoryModalVisible}
        selectedTask={selectedTask}
      />

      <TaskParameterModal
        isOpen={isParameterModalVisible}
        onClose={() => {
          setIsParameterModalVisible(false);
          setSelectedTaskForRun(null);
        }}
        onRunTask={handleRunTaskWithParameters}
        taskName={selectedTaskForRun?.name || ''}
        taskDescription={selectedTaskForRun?.description || ''}
        parameters={selectedTaskForRun?.parameters || []}
        loading={updating}
      />
    </PageSection>
  );
};

export default TasksManagement;
